import {
  AddEventListernerResult,
  Colors,
  useEventListenersRef,
  useMediaStreamPlaysSound,
  useRefAutoUpdate,
  Video,
  VideoEventListenerMap,
  WithNativeHTMLProps,
} from "@kalyzee/kast-web-sdk/web-components";
import { Meeting, MeetingSession } from "@kalyzee/kast-web-sdk/websocket";
import React, { ForwardedRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { ReactComponent as IconNoAudio } from "../../assets/icons/blackCutMicrophone.svg";
import { ReactComponent as IconNoVideo } from "../../assets/icons/no-video.svg";

import { ReactZoomPanPinchProps, TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { getAudioContextForVolumeAnalyzer } from "../../helpers/audio";
import { PlayerContainerRenderer } from "./interfaces";
import { MeetingPlayerDataOverlay } from "./MeetingPlayerDataOverlay";
import MeetingPlayerSoundEffectOverlay from "./MeetingPlayerSoundEffectOverlay";
import styles from "./MeetingSessionMediaStreamPlayer.module.css";

export interface MeetingSessionMediaStreamPlayerEventListenerMap {
  playingSound: (playing: boolean) => void;
}

export interface MeetingSessionMediaStreamPlayerProps extends WithNativeHTMLProps<HTMLDivElement> {
  meeting: Meeting;
  sessionId: string;
  mediaSessionId?: string;
  mediaStream?: MediaStream;
  videoIsDisabled?: boolean;
  muted?: boolean;
  hideSoundEffect?: boolean;
  forceUseSession?: MeetingSession;
  zoomPanPinch?: boolean | Omit<ReactZoomPanPinchProps, "ref">;
  callbacks?: Partial<MeetingSessionMediaStreamPlayerEventListenerMap>;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  renderCustomContainer?: PlayerContainerRenderer;
}

export interface MeetingSessionMediaStreamPlayerRef {
  isPlayingSound: () => boolean;
  addEventListener: <K extends keyof MeetingSessionMediaStreamPlayerEventListenerMap>(
    key: K,
    callback: MeetingSessionMediaStreamPlayerEventListenerMap[K]
  ) => AddEventListernerResult<MeetingSessionMediaStreamPlayerEventListenerMap, K>;
  removeEventListener: <K extends keyof MeetingSessionMediaStreamPlayerEventListenerMap>(
    key: K,
    callback: MeetingSessionMediaStreamPlayerEventListenerMap[K]
  ) => void;
}

const MemoVideo = React.memo(Video);

const MeetingSessionMediaStreamPlayer = React.forwardRef(
  (
    {
      meeting,
      sessionId,
      mediaSessionId,
      mediaStream,
      videoIsDisabled,
      muted,
      hideSoundEffect,
      forceUseSession,
      zoomPanPinch,
      callbacks,
      children,
      className,
      style,
      renderCustomContainer,
      nativeProps,
    }: MeetingSessionMediaStreamPlayerProps,
    forwardRef: ForwardedRef<MeetingSessionMediaStreamPlayerRef>
  ) => {
    const eventListenersRef = useEventListenersRef<keyof MeetingSessionMediaStreamPlayerEventListenerMap, MeetingSessionMediaStreamPlayerEventListenerMap>();
    const callbacksRef = useRefAutoUpdate(callbacks);
    const session = useMemo(() => forceUseSession ?? meeting.sessions?.find((s) => s.id === sessionId), [meeting, sessionId, forceUseSession]);
    const mediaSession = useMemo(() => session?.medias?.find((m) => m.id === mediaSessionId), [session, mediaSessionId]);
    const isPlayingSound = useMediaStreamPlaysSound(getAudioContextForVolumeAnalyzer(), mediaStream);
    const [videoIsReady, setVideoIsReady] = useState<boolean>(true);
    const videoCallbacks = useMemo<Partial<VideoEventListenerMap>>(
      () => ({
        ready: () => {
          setVideoIsReady(true);
        },
      }),
      [setVideoIsReady]
    );

    const videoStyles = useMemo<React.CSSProperties>(() => ({ objectFit: "contain" }), []);

    const getPosterUrl = useCallback(() => {
      let poster = mediaSession?.mediaThumbnail;
      if (poster) {
        // Force to reload poster after 10s (avoid cache)
        try {
          const url = new URL(poster);
          const now = Date.now();
          const UPDATE_EVERY_MS = 10000;
          url.searchParams.set('t', `${Math.floor(now / UPDATE_EVERY_MS) * UPDATE_EVERY_MS}`);
          poster = url.href;
        } catch(err) {}
      }
      return poster;
    }, [mediaSession?.mediaThumbnail]);
    const [poster, setPoster] = useState(getPosterUrl())

    // ---------------------------------------------------------------------- //
    // ---------------------------  UTILS ----------------------------- //
    // ---------------------------------------------------------------------- //

    // ---------------------------------------------------------------------- //
    // ---------------------------   EVENTS ----------------------------- //
    // ---------------------------------------------------------------------- //

    const triggerEvent = <K extends keyof MeetingSessionMediaStreamPlayerEventListenerMap>(
      key: K,
      ...args: Parameters<MeetingSessionMediaStreamPlayerEventListenerMap[K]>
    ) => {
      (callbacksRef.current?.[key] as any)?.call(null, ...args);
      eventListenersRef.current?.triggerEvent(key, ...args);
    };

    // ---------------------------------------------------------------------- //
    // ---------------------------  EFFECTS ----------------------------- //
    // ---------------------------------------------------------------------- //

    useImperativeHandle(
      forwardRef,
      () => ({
        isPlayingSound: () => isPlayingSound,
        addEventListener: <K extends keyof MeetingSessionMediaStreamPlayerEventListenerMap>(
          key: K,
          callback: MeetingSessionMediaStreamPlayerEventListenerMap[K]
        ) => eventListenersRef.current?.addEventListener(key, callback),
        removeEventListener: <K extends keyof MeetingSessionMediaStreamPlayerEventListenerMap>(
          key: K,
          callback: MeetingSessionMediaStreamPlayerEventListenerMap[K]
        ) => eventListenersRef.current?.removeEventListener(key, callback),
      }),
      [isPlayingSound]
    );

    useEffect(() => {
      if (!mediaStream) setVideoIsReady(false);
    }, [mediaStream]);

    useEffect(() => {
      triggerEvent("playingSound", isPlayingSound);
    }, [isPlayingSound]);

    // ---------------------------------------------------------------------- //
    // ---------------------------  RENDER ----------------------------- //
    // ---------------------------------------------------------------------- //

    const renderState = () => {
      return (
        <div className={styles.stateContainer}>
          {mediaSession && !mediaSession.video.enabled ? <IconNoVideo fill={Colors.getTorchRed()} width={30} height={30} /> : null}
          {mediaSession && !mediaSession.audio.enabled ? <IconNoAudio className={styles.mute} width={30} height={30} /> : null}
        </div>
      );
    };

    const renderContent = () => {
      const classes = ["meetingsessionmediaplayer-container", styles.container];
      if (className) classes.push(className);

      
      const videoElement = (
        <MemoVideo
          poster={poster}
          autoPlay
          controls={false}
          className={styles.video}
          src={mediaStream}
          muted={muted}
          callbacks={videoCallbacks}
          style={videoStyles}
          playsInline
          onWaiting={(e) => {
            setPoster(getPosterUrl());
          }}
        />
      );

      return (
        <div {...(nativeProps ?? {})} className={classes.join(" ")} style={style}>
          {zoomPanPinch ? (
            <TransformWrapper {...(typeof zoomPanPinch === "boolean" ? {} : zoomPanPinch)} minScale={1} maxScale={3} wheel={{ step: 0.05 }}>
              <TransformComponent wrapperStyle={{ width: "100%", height: "100%", background: "black" }} contentStyle={{ width: "100%", height: "100%" }}>
                {videoElement}
              </TransformComponent>
            </TransformWrapper>
          ) : (
            videoElement
          )}
          <MeetingPlayerDataOverlay
            meeting={meeting}
            session={session}
            mediaSession={mediaSession}
            mediaStream={mediaStream}
            videoIsDisabled={videoIsDisabled}
            videoIsReady={videoIsReady}
          />
          {renderState()}
          {children}
          <MeetingPlayerSoundEffectOverlay enabled={!hideSoundEffect && isPlayingSound} />
        </div>
      );
    };

    if (renderCustomContainer) {
      return <>{renderCustomContainer(renderContent(), { isPlayingSound })}</>;
    }

    return renderContent();
  }
);

export default MeetingSessionMediaStreamPlayer;
